import {
    ApiStatus,
    DropDownOptions,
    RadioOption,
} from '../../types';

export enum GenericBladeType {
    dropdown,
    input,
    confirm,
    selectiveInput,
    timeDuration,
    radio
}

export interface GenericDropdownAPI {
    options?: DropDownOptions<string>[];
    selectedValue?: string;
}

export interface GenericInputAPI {
    value?: string;
}

export interface GenericBladeAPI {
    content?: string;
    genericDropdownAPI?: Record<string, GenericDropdownAPI>;
    genericInputAPI?: Record<string, GenericInputAPI>;
    value?: Record<string, string>;
    years?: string;
    months?: string;
}

export interface GenericRadioForm {
    label?: string,
    options: RadioOption[];
}

export interface GenericValidation {
    minValue?: number | string;
    minValueMessage?: string;
    maxValue?: number | string;
    maxValueMessage?: string;
    baseValue?: number | string;
    baseValueMessage?: string;
    negativeValueMessage?: string;
    invalidAmountMessage?: string;
    defaultErrorMessage?: string;
    maxLength?: number;
}

export type GenericValidationType = {
    [key: string]: string | undefined;
}

export interface ValidationInfo {
    hardValidations?: Record<string, GenericValidationType>;
    softValidations?: Record<string, GenericValidationType>;
}

export interface GenericFormAEM {
    heading?: string;
    description?: string;
    label?: string;
    defaultValue?: string;
    fieldType?: 'currency' | 'number' | 'text' | 'email' | 'password';
    dropdownOptions?: DropDownOptions<string>[];
    showFormDivider?: boolean;
    hideInput?: string[];
    validations?: ValidationInfo;
    hiddenInputs?: Record<string, GenericBladeHiddenInput>;
}

export type GenericBladeHiddenInput = {
    label?: string,
    values: string[]
}

export interface GenericBladeStaticContentInfo {
    [key: string]: GenericBladeStaticContentType;
}

export interface GenericBladeDynamicContentInfo {
    [key: string]: GenericBladeDynamicContentType;
}

export interface GenericBladeStateParams {
    heading?: string[];
    description?: string[];
}

export interface GenericBladeVariableAEM {
    [key: string]: GenericBladeStaticContentType | GenericBladeDynamicContentType;
}

export interface GenericBladeAEM extends GenericBladeVariableAEM {
    heading: string;
    description?: string;
    callouts?: string[];
    calloutParams?: string[];
    notificationHeading?: string;
    notificationText?: string;
    stepsHeading?: string;
    steps?: string[];
    stepsDescription?: string[];
    stepsFooter?: string;
    confirmButtonText: string;
    confirmButtonLink?: string;
    cancelButtonText?: string;
    cancelButtonLink?: string;
    validations?: ValidationInfo;
    hiddenInputs?: Record<string, GenericBladeHiddenInput>;
    img?: string
}

export interface GenericBladeInitParam {
    genericBladeType: GenericBladeType;
    urlFetchEndPoint?: string;
    urlSubmitEndPoint?: string;
    urlCancelEndPoint?: string;
    content: GenericBladeAEM;
    api?: GenericBladeAPI;
    id: string;
    contentParams?: GenericBladeStateParams;
}

export interface GenericBladeInfo {
    apiFetchStatus: ApiStatus;
    apiSubmitStatus: ApiStatus;
    apiCancelStatus?: ApiStatus;
    genericBladeType: GenericBladeType;
    urlFetchEndPoint?: string;
    urlSubmitEndPoint: string;
    urlCancelEndPoint?: string;
    content: GenericBladeAEM;
    api?: GenericBladeAPI;
    submittedPayload?: SubmittedPayload;
    cancelPayload?: CancelPayload;
    apiFetchErrorMessage?: string;
    apiSubmitErrorMessage?: string;
    apiCancelErrorMessage?: string;
    contentParams?: GenericBladeStateParams;
    selectedValue?: NodePayload;
}

export type NodePayload = boolean | string | number;
export type SubmittedPayload = Record<string, NodePayload | null>;
export type CancelPayload = Record<string, NodePayload | null>;
export type GenericBladeParsedDataType = Record<string, GenericBladeParsedData>;
export type GenericBladeStaticContentType =
    | string
    | string[]
    | ValidationInfo
    | undefined;
export type GenericBladeDynamicContentType =
    | GenericFormAEM
    | DropDownOptions<string>[]
    | NodePayload
    | undefined;

export type GenericSelectiveInputDropdown = {
    key: string;
    options: DropDownOptions<string>[];
}

export interface GenericBladeFetchPayload {
    id: string;
    fetchPayload: GenericBladeAPI;
}

export interface GenericBladeParsedData {
    value?: NodePayload | null;
    options?: DropDownOptions<string>[];
}

export interface GenericBladeSubmitPayload {
    id: string;
    submittedPayload: SubmittedPayload;
}

export interface GenericBladeSubmitRejectedPayload {
    id: string;
    apiErrorMessage: string;
}

export interface GenericBladeCancelPayload {
    id: string;
    cancelPayload?: CancelPayload;
}

export type GenericBladeState = Record<string, GenericBladeInfo>;
